import axiosApiInstance from '../../api/axiosClient';
import {
    parseDdexUpc,
    parseNotificationBody,
} from './parseNotificationBody';
import type { InboxItem } from './types';

const createReleaseLinkRe = /^\/music\/create\/([^/?#]+)$/;

function releaseIdFromCreateLink(link?: string): string | undefined {
    const trimmed = link?.trim();
    if (!trimmed) return undefined;
    const m = trimmed.match(createReleaseLinkRe);
    return m?.[1];
}

/** Whether the UI should offer a related-page action (may still need API lookup on click). */
export function notificationHasRelatedTarget(item: InboxItem): boolean {
    if (releaseIdFromCreateLink(item.link)) return true;
    if (item.link?.trim()) return true;
    const parsed = parseNotificationBody(item.body, item.title);
    return parsed.kind === 'ddex' && !!parseDdexUpc(item.body);
}

export function notificationRelatedLinkLabel(item: InboxItem): string {
    const parsed = parseNotificationBody(item.body, item.title);
    if (parsed.kind === 'ddex') {
        return 'Open release editor';
    }
    return 'Open related page';
}

/**
 * Resolves navigation target for notification actions.
 * DDEX: `/music/create/{releaseId}` — looks up release id by UPC when the stored link is missing or wrong.
 */
export async function resolveNotificationRelatedLink(item: InboxItem): Promise<string | undefined> {
    const stored = item.link?.trim();
    const parsed = parseNotificationBody(item.body, item.title);
    const upc =
        parsed.kind === 'ddex'
            ? parseDdexUpc(item.body)
            : undefined;

    if (upc) {
        try {
            const { data } = await axiosApiInstance.get<API.MusicDistribution.MusicRelease>(
                `/api/products/releases/${encodeURIComponent(upc)}`,
                { params: { without_tracks: 'true' } },
            );
            if (data?.id) {
                return `/music/create/${data.id}`;
            }
        } catch {
            // fall through
        }
    }

    const createId = releaseIdFromCreateLink(stored);
    if (createId) {
        return `/music/create/${createId}`;
    }

    const legacyUpc = stored?.match(/^\/music\/([^/]+)\/edit$/)?.[1];
    if (legacyUpc) {
        try {
            const { data } = await axiosApiInstance.get<API.MusicDistribution.MusicRelease>(
                `/api/products/releases/${encodeURIComponent(legacyUpc)}`,
                { params: { without_tracks: 'true' } },
            );
            if (data?.id) {
                return `/music/create/${data.id}`;
            }
        } catch {
            // fall through
        }
    }

    return stored || undefined;
}
